import type React from 'react';
import { format, subDays } from 'date-fns';

import { DateInput, DatePicker, FormField, Input, Label } from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { NOTICES_UTILITY_TERMINATION } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';
import YesAndNo from '../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function NoticesOfCodeViolationsAndUtilityTermination({ section, setSectionValues }: Props) {
  function onNoNotices(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.IS_ENABLED]: false,
    });
  }

  function onYesNotices(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onUtilityServiceChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.UTILITY_SERVICE]: value,
    });
  }

  function onTerminationDateChange(formattedDate: string): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.DATE_OF_TERMINATION]: formattedDate,
    });
  }

  function onNoUtilityTerminationCommon(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.TERMINATION_AFFECT_COMMON_AREA]: false,
    });
  }

  function onYesUtilityTerminationCommon(): void {
    setSectionValues({
      [NOTICES_UTILITY_TERMINATION.FIELD_NAME.TERMINATION_AFFECT_COMMON_AREA]: true,
    });
  }

  return (
    <FormField
      required
      label={
        <Label>
          In the past 12 months, have you received any notices from municipalities or utility providers of utility
          termination?
        </Label>
      }
      control={
        <YesAndNoExpansion
          yesHandler={onYesNotices}
          noHandler={onNoNotices}
          yesActive={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            required
            css={{ marginTop: 'layout.tighter' }}
            label={<Label>Utility service to be terminated</Label>}
            control={
              <Input
                value={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.UTILITY_SERVICE)}
                onChange={onUtilityServiceChange}
              />
            }
          />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>Date of utility service termination</Label>}
            control={
              <DatePicker
                dateFormat={DATE_PICKER_FORMAT}
                value={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.DATE_OF_TERMINATION)}
                onValueChange={(value) => {
                  const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
                  onTerminationDateChange(formatted);
                }}
                minDate={subDays(new Date(), 1)}
                closeModalOnDateSelect
                input={<DateInput />}
              />
            }
          />
          <FormField
            required
            label={<Label>Will the termination of the utility service affect the property or common areas?</Label>}
            control={
              <YesAndNo
                yesHandler={onYesUtilityTerminationCommon}
                noHandler={onNoUtilityTerminationCommon}
                yesActive={section.getFieldValue(NOTICES_UTILITY_TERMINATION.FIELD_NAME.TERMINATION_AFFECT_COMMON_AREA)}
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}

NoticesOfCodeViolationsAndUtilityTermination.description = 'Chicago specific screen';

export default NoticesOfCodeViolationsAndUtilityTermination;
